/**
 * Assistant run event types (v1).
 *
 * Hand-derived from and kept byte-faithful to the NORMATIVE contract at
 * `.contracts/assistant-events-v1.schema.json` (JSON Schema 2020-12). That
 * schema is the single source of truth; these types exist so the reducer,
 * validator and (later) transport share one compile-time shape. The contract
 * is LOCKED, so hand-authoring beats a codegen step here — the discriminated
 * unions read far cleaner than a generator's `oneOf` output, and there is no
 * churn to chase.
 *
 * The logical envelope is `{schemaVersion, seq, type, data}`. On the wire it
 * splits into the SSE `id` (= seq / throughSeq), `event` (= type) and JSON
 * body (= data); reconstructing the logical object is the transport's job.
 * Three frame families ride the stream:
 *   1. durable, sequenced events appended to the run log,
 *   2. the live-only, seq-less family: the `message.delta` preview and the
 *      CC-426 `tool.started` step line — best-effort, never replayed on
 *      reconnect,
 *   3. the synthetic `run.snapshot` frame (sits outside the durable log; its
 *      `throughSeq` is the reduction boundary).
 */

// ---------------------------------------------------------------------------
// Shared enums / primitives
// ---------------------------------------------------------------------------

/** Disclosure sensitivity label carried on most projected payloads. */
export type Sensitivity = "NORMAL" | "SENSITIVE" | "FINANCIAL";

/** State of a single typed diff value once projected for the viewer. */
export type ValueState = "VALUE" | "NULL" | "MASKED";

/** Pre-resolved EN/AR fallback strings for a label the client cannot key. */
export interface LocalizedFallback {
  en: string;
  ar: string;
}

/** SSE `event` name for the synthetic snapshot frame (has no `type` in body). */
export const SNAPSHOT_EVENT_NAME = "run.snapshot" as const;

/** The live-only preview frame `type`. */
export const LIVE_DELTA_TYPE = "message.delta" as const;

/** The live-only CC-426 step-start frame `type` (seq-less, never replayed). */
export const LIVE_TOOL_STARTED_TYPE = "tool.started" as const;

/** Every durable (sequenced) event `type`, in schema declaration order. */
export const DURABLE_EVENT_TYPES = [
  "run.queued",
  "run.started",
  "run.requeued",
  "run.completed",
  "run.failed",
  "run.cancelled",
  "run.expired",
  "model.completed",
  "tool.completed",
  "tool.failed",
  "confirmation.requested",
  "confirmation.resolved",
  "confirmation.expired",
  "message.completed",
] as const;

export type DurableEventType = (typeof DURABLE_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Redaction markers
// ---------------------------------------------------------------------------

/**
 * Emitted in place of an event/entry payload when the caller no longer holds
 * the disclosure permission the content was labeled with. Used for every
 * durable event EXCEPT `message.completed`, which carries a type-preserving
 * redaction of its own (see `MessageCompletedRedacted`).
 */
export interface RedactedMarker {
  redacted: true;
  type: string;
  reason: "insufficient_permissions";
}

// ---------------------------------------------------------------------------
// Durable event data payloads
// ---------------------------------------------------------------------------

export interface RunQueuedData {
  status: string;
}
export interface RunStartedData {
  status: "RUNNING";
}
export interface RunRequeuedData {
  reason: string;
}
export interface RunCompletedData {
  status: string;
}
export interface RunFailedData {
  errorCode: string;
}
/**
 * A terminal cancel carries `errorCode`; an immediate/content-removed cancel
 * carries `status` (and optional `reason`). All three are optional per schema.
 */
export interface RunCancelledData {
  status?: string;
  reason?: string;
  errorCode?: string;
}
export interface RunExpiredData {
  errorCode: string;
}
export interface ModelCompletedData {
  iteration: number;
  stopReason: string;
}
/**
 * CC-426 shapes. On the wire the contract requires `label` (server-resolved,
 * localized — render verbatim, never a key, never a raw tool id). `label` is
 * widened to `string | null` here because pre-CC-426 durable events persist in
 * stored conversations FOREVER: the validator normalizes a legacy
 * `{id, tool, sensitivity}` payload to `{invocationId: id, label: null, ...}`
 * (the raw tool id must never stand in for a label — it leaks English).
 */
export interface ToolCompletedData {
  /** Opaque per-invocation correlation id; matches tool.started + the snapshot card. */
  invocationId: string;
  label: string | null;
  sensitivity: Sensitivity;
}
export interface ToolFailedData {
  /** null when normalized from a legacy frame (which carried no stable id). */
  invocationId: string | null;
  label: string | null;
  error: string;
}
export interface ToolStartedData {
  invocationId: string;
  /** Server-resolved localized step line ("checking your leave balance…"). */
  label: string;
}
export interface ConfirmationResolvedData {
  status: string;
}
export interface ConfirmationExpiredData {
  confirmationId: string;
}

/** Quick-reply / disambiguation intent (CC-357). Absent ⇒ `quick_reply`. */
export type AssistantQuickReplyKind = "quick_reply" | "subject_confirm";

/**
 * A server-authored quick-reply chip offered on a completed assistant message
 * (CC-354 + CC-357). A tap submits `label` VERBATIM as a normal turn — `id` and
 * `sublabel` are never submitted. `sublabel` (≤160) is presentation-only.
 * `kind: "subject_confirm"` marks an identity-confirmation chip (a tap IS the
 * subject confirmation) and is wire-distinct from the proposal approve/reject
 * flow. Redacted messages carry NO choices.
 */
export interface AssistantQuickReplyChoice {
  id: string;
  label: string;
  sublabel?: string;
  kind?: AssistantQuickReplyKind;
}

/**
 * One ordered content block of a completed answer (CC-358), discriminated by
 * `kind`. `MODEL_MARKDOWN` is the model's vetted prose (the SAME string `text`
 * carries — when blocks are present the first block repeats it). `SERVER_TABLE`
 * is a server-composed GFM table the model never saw; it is permission-filtered
 * per viewer SERVER-side, so the client just renders whatever arrives (the
 * `sensitivity` / `requiredPermissions` fields are informational only here).
 * `SERVER_MARKDOWN` is server-composed prose (for example a letter preview) the
 * model never saw, filtered per viewer the same way. Unknown future kinds MUST
 * be ignored, not fail the message. When `blocks` is
 * present, render the blocks IN ORDER instead of `text` (never both). Absent on
 * the redacted carrier.
 */
export type AssistantContentBlock =
  | { kind: "MODEL_MARKDOWN"; text: string }
  | {
      kind: "SERVER_TABLE";
      markdown: string;
      sensitivity?: Sensitivity;
      requiredPermissions?: readonly string[];
    }
  | {
      // CC-557: server-composed prose the model never saw (for example a letter
      // preview), markdown-escaped server-side and permission-filtered per
      // viewer exactly like SERVER_TABLE — render whatever arrives. Unlike
      // SERVER_TABLE, both `sensitivity` and `requiredPermissions` are
      // contract-MANDATORY (a block that cannot say who may see it is never
      // emitted).
      kind: "SERVER_MARKDOWN";
      markdown: string;
      sensitivity: Sensitivity;
      requiredPermissions: readonly string[];
    }
  | {
      // CC-361: a server-composed in-app link to a report the assistant just ran.
      // `relativeRoute` is ALWAYS `/reports?jobId={uuid}` (schema-pinned) — route
      // it INTERNALLY, never fetch it as a URL. `label` is server-authored + already
      // localized. Permission-filtered per viewer server-side.
      kind: "REPORT_LINK";
      relativeRoute: string;
      label: string;
      requiredPermissions?: readonly string[];
    }
  | {
      // CC-427: a card of money the answer is ABOUT, every figure withheld.
      // `masked` is ALWAYS the literal "•••" (never sized to the value — no
      // digit-count leak); render it verbatim. `revealTicket` redeems ONCE via
      // POST /assistant/amount-reveals for exact decimal STRINGS (never parse
      // as floats). Whole-card reveal; hold the values client-side — a replay
      // answers ASSISTANT_AMOUNT_TICKET_SPENT, never the figures again.
      // `expiresAt` is informational (lazy expiry per fe-449).
      kind: "MASKED_AMOUNT";
      title: string;
      currency: string;
      rows: readonly { label: string; masked: string }[];
      revealTicket: string;
      expiresAt: string;
      requiredPermissions: readonly string[];
    }
  | {
      // CC-428: a server-composed TYPED deep link — no URLs on the wire. The
      // CLIENT owns the routeId → destination mapping; a routeId the table
      // does not know renders NOTHING (contract-open vocabulary, same
      // forward-compat as unknown kinds). `entityId` is ABSENT for
      // screen-level routes. `label` is server-localized and names the
      // destination, never the record.
      kind: "ENTITY_LINK";
      routeId: string;
      entityId?: string;
      label: string;
      requiredPermissions: readonly string[];
    }
  | {
      // be-893: ENTITY_LINK repeated — one routeId, many entities. Emitted on
      // import receipts so a finished import lists the people it created, each
      // a tappable profile link. Labels are server-localized (name + employee
      // code); the list is capped server-side at 30 and the prose counters
      // carry the true total.
      kind: "ENTITY_LINK_LIST";
      routeId: string;
      items: readonly { entityId: string; label: string }[];
      requiredPermissions: readonly string[];
    }
  | {
      // The report builder's draft (REPORT_BUILDER surface only): the latest
      // previewed definition of the run and its server-derived name. The
      // definition is OPAQUE here — the builder reads it with its own reader,
      // and a draft it can't read shows nothing. `requiredPermissions` is the
      // same clause list as SERVER_TABLE. Agreed with the backend; not in the
      // published events schema yet.
      kind: "REPORT_DRAFT";
      definition: Readonly<Record<string, unknown>>;
      name: string;
      requiredPermissions?: readonly string[];
    };

/** One report-builder draft block (see the union above). */
export type AssistantReportDraftBlock = Extract<AssistantContentBlock, { kind: "REPORT_DRAFT" }>;

/**
 * Final USER_VISIBLE answer for a caller that holds the message's disclosure
 * label. `messageSeq` is the persisted assistant_message.seq (the snapshot
 * Message.seq space, NOT the durable event seq), so a reduction reconstructs
 * the identically message-ordered snapshot Message. `generationId` is optional
 * for legacy/replayed completions persisted before previews existed. `choices`
 * (CC-357) is optional server-authored quick replies; `blocks` (CC-358) is the
 * optional ordered answer-block list. Sanitize both before use.
 */
export interface MessageCompletedData {
  messageId: string;
  generationId?: string;
  messageSeq: number;
  text: string;
  sensitivity: Sensitivity;
  choices?: readonly AssistantQuickReplyChoice[];
  blocks?: readonly AssistantContentBlock[];
}

/**
 * Type-preserving redaction of `message.completed` for a caller missing the
 * message's disclosure label: keeps identity + sensitivity (text null) so a
 * reduction reconstructs the SAME identifiable redacted snapshot Message
 * rather than an opaque marker.
 */
export interface MessageCompletedRedacted {
  messageId: string;
  generationId?: string;
  messageSeq: number;
  text: null;
  sensitivity: Sensitivity;
  redacted: true;
}

// ---------------------------------------------------------------------------
// Typed diff value union (discriminated by `type`)
// ---------------------------------------------------------------------------

/** Money amount STAYS a string end-to-end (never parsed to a JS number). */
export interface MoneyValue {
  type: "money";
  state: ValueState;
  amount?: string | null;
  currency?: string | null;
}
export interface NumberValue {
  type: "number";
  state: ValueState;
  value?: number | null;
  unit?: string | null;
}
export interface DateValue {
  type: "date";
  state: ValueState;
  /** ISO date-only string. */
  value?: string | null;
}
export interface TimeValue {
  type: "time";
  state: ValueState;
  /** Wall-clock time string (no timezone). */
  value?: string | null;
}
export interface EnumValue {
  type: "enum";
  state: ValueState;
  code?: string | null;
  labelKey?: string | null;
  labelFallback?: LocalizedFallback | null;
}
export interface EntityRefValue {
  type: "entityRef";
  state: ValueState;
  entityType?: string | null;
  id?: string | null;
  displayText?: string | null;
}
export interface TextValue {
  type: "text";
  state: ValueState;
  value?: string | null;
}
export interface BooleanValue {
  type: "boolean";
  state: ValueState;
  value?: boolean | null;
}
export interface ListValue {
  type: "list";
  state: ValueState;
  items?: DiffValue[] | null;
}

export type DiffValue =
  | MoneyValue
  | NumberValue
  | DateValue
  | TimeValue
  | EnumValue
  | EntityRefValue
  | TextValue
  | BooleanValue
  | ListValue;

// ---------------------------------------------------------------------------
// Proposal view (confirmation.requested + AWAITING_CONFIRMATION snapshot)
// ---------------------------------------------------------------------------

export type DiffOperation = "ADD" | "REPLACE" | "REMOVE";
export type DiffDisplayPolicy = "FULL" | "MASKED" | "OMITTED";

/**
 * One field-level change, already projected for the viewer. ADD: before null.
 * REMOVE: after null. REPLACE: both present and the same DiffValue type.
 */
export interface DiffEntryView {
  /** RFC 6901 JSON Pointer. */
  path: string;
  operation: DiffOperation;
  labelKey: string;
  labelFallback: LocalizedFallback;
  before: DiffValue | null;
  after: DiffValue | null;
  sensitivity: Sensitivity;
  displayPolicy: DiffDisplayPolicy;
  /**
   * CC-481: `"current"` marks a server-read current-state row (the card folds
   * these); `null` or absent marks a proposal row (action, target, values).
   * Additive; proposals frozen before that build simply omit it.
   */
  group?: "current" | null;
}

export interface ProposalDiffView {
  schemaVersion: number;
  entries: DiffEntryView[];
}

/**
 * Public permission-projected view of a pending proposal, serialized
 * identically in `confirmation.requested` and the AWAITING_CONFIRMATION
 * snapshot. The canonical command payload is NEVER part of it.
 */
export interface ProposalView {
  confirmationId: string;
  proposalId: string;
  commandType: string;
  proposalHash: string;
  /** ISO date-time. */
  expiresAt: string;
  status: string;
  diff: ProposalDiffView;
  warnings: string[];
  sideEffects: string[];
  /** Pre-localized card title (CC-447). Optional: older events lack it, and
   * the published events schema has not caught up — flagged to the backend. */
  title?: string;
}

// ---------------------------------------------------------------------------
// Durable envelope + concrete frames
// ---------------------------------------------------------------------------

export interface DurableEnvelope<TType extends DurableEventType, TData> {
  schemaVersion: 1;
  /** Monotonic per-run sequence number. */
  seq: number;
  type: TType;
  data: TData;
}

export type FrameRunQueued = DurableEnvelope<"run.queued", RunQueuedData | RedactedMarker>;
export type FrameRunStarted = DurableEnvelope<"run.started", RunStartedData | RedactedMarker>;
export type FrameRunRequeued = DurableEnvelope<"run.requeued", RunRequeuedData | RedactedMarker>;
export type FrameRunCompleted = DurableEnvelope<"run.completed", RunCompletedData | RedactedMarker>;
export type FrameRunFailed = DurableEnvelope<"run.failed", RunFailedData | RedactedMarker>;
export type FrameRunCancelled = DurableEnvelope<"run.cancelled", RunCancelledData | RedactedMarker>;
export type FrameRunExpired = DurableEnvelope<"run.expired", RunExpiredData | RedactedMarker>;
export type FrameModelCompleted = DurableEnvelope<
  "model.completed",
  ModelCompletedData | RedactedMarker
>;
export type FrameToolCompleted = DurableEnvelope<
  "tool.completed",
  ToolCompletedData | RedactedMarker
>;
export type FrameToolFailed = DurableEnvelope<"tool.failed", ToolFailedData | RedactedMarker>;
export type FrameConfirmationRequested = DurableEnvelope<
  "confirmation.requested",
  ProposalView | RedactedMarker
>;
export type FrameConfirmationResolved = DurableEnvelope<
  "confirmation.resolved",
  ConfirmationResolvedData | RedactedMarker
>;
export type FrameConfirmationExpired = DurableEnvelope<
  "confirmation.expired",
  ConfirmationExpiredData | RedactedMarker
>;
export type FrameMessageCompleted = DurableEnvelope<
  "message.completed",
  MessageCompletedData | MessageCompletedRedacted
>;

/** Discriminated union of every durable, sequenced event. */
export type DurableEvent =
  | FrameRunQueued
  | FrameRunStarted
  | FrameRunRequeued
  | FrameRunCompleted
  | FrameRunFailed
  | FrameRunCancelled
  | FrameRunExpired
  | FrameModelCompleted
  | FrameToolCompleted
  | FrameToolFailed
  | FrameConfirmationRequested
  | FrameConfirmationResolved
  | FrameConfirmationExpired
  | FrameMessageCompleted;

// ---------------------------------------------------------------------------
// Live-only delta preview
// ---------------------------------------------------------------------------

/**
 * Live preview for one generation. `text` is an APPEND-ONLY chunk (never
 * cumulative); `streamOrdinal` is the 0-based contiguous per-generation
 * ordinal. Exactly ONE generation is active per run at a time. Deltas are
 * NEVER emitted for a generation whose completion would be redacted for the
 * subscriber — there is no redacted delta shape.
 */
export interface MessageDeltaData {
  messageId: string;
  generationId: string;
  streamOrdinal: number;
  text: string;
}

export interface LiveDelta {
  schemaVersion: 1;
  type: typeof LIVE_DELTA_TYPE;
  data: MessageDeltaData;
}

/**
 * Live-only CC-426 step start. Best-effort by design: a client connecting
 * mid-capability receives no start and learns of the step from the snapshot's
 * tool card when it completes. A viewer without the capability's permissions
 * receives no frame at all (there is no redacted shape). Any terminal run
 * event supersedes every outstanding step line.
 */
export interface LiveToolStarted {
  schemaVersion: 1;
  type: typeof LIVE_TOOL_STARTED_TYPE;
  data: ToolStartedData;
}

// ---------------------------------------------------------------------------
// Snapshot frame + snapshot state
// ---------------------------------------------------------------------------

export interface SnapshotMessage {
  id: string;
  seq: number;
  role: string;
  /** null when `redacted === true`. */
  text: string | null;
  sensitivity: Sensitivity;
  redacted: boolean;
  /**
   * Server-authored quick replies (CC-357), projected only for a non-redacted
   * assistant message that offered them. Always run through `sanitizeChoices`
   * before it reaches this field — a partial/garbled set fails closed to
   * undefined (never a subset).
   */
  choices?: readonly AssistantQuickReplyChoice[];
  /**
   * Ordered answer blocks (CC-358), projected only for a non-redacted assistant
   * message that carries them. Run through `sanitizeBlocks` first (unknown kinds
   * dropped, a structurally-broken set falls back to `text`). When present,
   * render these IN ORDER instead of `text`.
   */
  blocks?: readonly AssistantContentBlock[];
}

/**
 * In-flight generation preview reconstructed from the checkpoint, projected
 * for the viewer. `redacted === true` (mirrors the message shape) means the
 * caller lacks the disclosure permission and `text` is null. `incomplete` is
 * always true (a live preview).
 */
export interface SnapshotGeneration {
  messageId: string;
  generationId: string;
  streamOrdinal: number;
  /** null when `redacted === true`. */
  text: string | null;
  sensitivity: Sensitivity;
  redacted: boolean;
  incomplete: true;
}

/** One completed step; same values the tool.completed event carried (CC-426).
 * `label` null only when normalized from a legacy pre-CC-426 card. */
export interface SnapshotToolCard {
  invocationId: string;
  label: string | null;
  sensitivity: Sensitivity;
}

export interface SnapshotFactCard {
  id: string;
  tool: string;
  sensitivity: Sensitivity;
  rowsReturned: number | null;
}

/**
 * The reduced, current-permission-projected run state at `throughSeq`.
 * `factCards` is ALWAYS an empty array in v1 (clients reserve the slot).
 * `currentGeneration` is the checkpointed in-flight preview or null; a
 * any `message.completed` supersedes it because exactly one generation is
 * active per run.
 * `proposal` is the FULL projected ProposalView when AWAITING_CONFIRMATION,
 * else null.
 */
export interface SnapshotState {
  status: string;
  messages: SnapshotMessage[];
  currentGeneration: SnapshotGeneration | null;
  toolCards: SnapshotToolCard[];
  factCards: SnapshotFactCard[];
  proposal: ProposalView | null;
  terminalError: string | null;
}

/**
 * Synthetic frame delivered when a client connects WITHOUT a cursor. Never
 * persisted as a durable event; its `throughSeq` is the reduction boundary
 * (the client reduces this, then applies durable events with seq > throughSeq).
 * Carries no `type` in its body — the SSE `event` name is `run.snapshot`.
 */
export interface SnapshotFrame {
  schemaVersion: 1;
  runId: string;
  /** -1 when the run has no durable events yet. */
  throughSeq: number;
  state: SnapshotState;
}

// ---------------------------------------------------------------------------
// Top-level union
// ---------------------------------------------------------------------------

/** Any logical frame the client may receive on the assistant stream. */
export type AssistantFrame = SnapshotFrame | LiveDelta | LiveToolStarted | DurableEvent;
